import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'next-themes'
import { toast } from 'sonner'
import {
  Check,
  Settings as SettingsIcon,
  Sun,
  Moon,
  Monitor,
  Languages,
  LogOut,
  Plus,
  Loader2,
  type LucideIcon,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppStore } from '@/store'
import { USER_AVATAR_GRADIENT_CLASS } from '@/utils/userUtils'
import {
  changeLanguage,
  getCurrentLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/i18n/config'
import { cn } from '@/lib/utils'

const THEME_OPTIONS: {
  id: 'light' | 'dark' | 'system'
  icon: LucideIcon
  labelKey: string
}[] = [
  { id: 'light', icon: Sun, labelKey: 'common:settings.appearance.light' },
  { id: 'dark', icon: Moon, labelKey: 'common:settings.appearance.dark' },
  {
    id: 'system',
    icon: Monitor,
    labelKey: 'common:settings.appearance.system',
  },
]

type UserMenuProps = {
  tooltipSide: 'left' | 'right'
}

/** Circular org/workspace avatar — muted circle with the org's initial, matching
 * the Figma "Avatar / Image" treatment (we have no per-org logo asset). */
function OrgAvatar({ name }: { name: string }) {
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || 'O'
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
      {initial}
    </div>
  )
}

/**
 * Avatar menu in the left rail (Figma node 13427:193402, with the org list
 * 13427:193375 linked as a side flyout). The active-org header row is a submenu
 * trigger: hovering (or clicking) it opens the organization list to the side,
 * with the active org checked, plus a (disabled) "Create New Workspace" row.
 * Below it: Appearance, Language, Settings, Log out. Switching org calls
 * authSessionSlice.switchTenant (Keycloak switch + reload).
 */
export default function UserMenu({ tooltipSide }: UserMenuProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()

  const user = useAppStore((s) => s.user)
  // The live store always inits `organizations` to an array, but this is
  // top-level chrome: guard so a not-yet-hydrated (or partially-mocked) store
  // can never crash the whole app at `organizations.find(...)` below.
  const organizationsRaw = useAppStore((s) => s.organizations)
  const organizations = Array.isArray(organizationsRaw) ? organizationsRaw : []
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const switchingTenant = useAppStore((s) => s.switchingTenant)
  const switchTenant = useAppStore((s) => s.switchTenant)
  const logout = useAppStore((s) => s.logout)

  const [open, setOpen] = useState(false)

  // FS-693: the photo is a remote URL owned by the identity provider (Google
  // rotates and expires them), so a 404 is a normal end state, not an error to
  // surface. Fall back to the initial rather than leaving a broken image in the
  // rail.
  //
  // Keyed on the whole `user`, not `user.picture`: a transient failure would
  // otherwise stick for the component's lifetime, and this is top-level chrome
  // that never unmounts. authSessionSlice rebuilds `user` on every auth
  // refresh, so each refresh retries the URL — including the same URL, which is
  // exactly the case a network blip leaves behind. It is a new object per
  // refresh and stable between them, so this retries on refresh rather than on
  // every render.
  const [pictureFailed, setPictureFailed] = useState(false)
  useEffect(() => {
    setPictureFailed(false)
  }, [user])
  const picture = pictureFailed ? undefined : user?.picture

  const userInitial =
    user?.given_name?.charAt(0)?.toUpperCase() ||
    user?.name?.charAt(0)?.toUpperCase() ||
    'U'

  const activeOrg =
    organizations.find((o) => o.id === activeOrganizationId) ?? organizations[0]
  const currentLang = getCurrentLanguage()
  const langName = (code: string): string =>
    t(`nav:language_switcher.languages.${code}`) as string

  const handleSwitch = async (orgId: string) => {
    if (orgId === activeOrganizationId || switchingTenant) return
    try {
      // switchTenant reloads the SPA on success; the catch only fires on failure
      // (Keycloak switch endpoint 401/403, network error, etc.).
      await switchTenant(orgId)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.error('[tenant-switch] failed:', err)
      toast.error(t('account:tenant.switch_failed'), { description: message })
    }
  }

  // Prefer the human-readable label the user entered at org creation.
  // Fall back to `name` (Keycloak-unique slug) only when displayName is
  // missing — that happens on legacy orgs created before the displayName
  // attribute was wired up.
  const activeOrgLabel = activeOrg?.displayName || activeOrg?.name

  // The active-org header. When the user belongs to >1 org it's a submenu
  // trigger (hover/click opens the org list to the side); otherwise it's a
  // static, non-interactive label.
  const orgHeaderContent = (
    <>
      <OrgAvatar name={activeOrgLabel || user?.name || 'O'} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {activeOrgLabel || user?.name || t('account:title')}
      </span>
    </>
  )

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex size-12 items-center justify-center overflow-hidden rounded-full text-sm font-normal text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400',
                USER_AVATAR_GRADIENT_CLASS
              )}
              aria-label={t('common:tooltips.account')}
            >
              {switchingTenant ? (
                <Loader2 className="size-5 animate-spin" />
              ) : picture ? (
                // The gradient stays underneath as the backdrop while the photo
                // loads, so the rail never flashes an empty circle.
                <img
                  src={picture}
                  alt=""
                  className="size-full object-cover"
                  referrerPolicy="no-referrer"
                  draggable={false}
                  onError={() => setPictureFailed(true)}
                />
              ) : (
                userInitial
              )}
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        {/* Hide the tooltip while the menu is open so the two don't overlap. */}
        {!open && (
          <TooltipContent side={tooltipSide}>
            <p>{user?.name || t('common:tooltips.account')}</p>
          </TooltipContent>
        )}
      </Tooltip>

      <DropdownMenuContent
        side={tooltipSide}
        align="end"
        className="w-[250px] p-1"
      >
        {/* Active org header — always a submenu trigger that opens the
            organization list (plus "Create New Workspace") to the side on
            hover/click (Figma 13427:193375), regardless of how many orgs the
            user belongs to. Single-org users still get the switcher flyout and
            the create-workspace entry. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer gap-1.5 px-1 py-1.5">
            {orgHeaderContent}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-[230px] p-1">
            {organizations.map((org) => {
              const isActive = org.id === activeOrganizationId
              const orgLabel = org.displayName || org.name
              return (
                <DropdownMenuItem
                  key={org.id}
                  disabled={switchingTenant}
                  className="cursor-pointer gap-1.5 px-1 py-1.5"
                  onSelect={(e) => {
                    e.preventDefault()
                    void handleSwitch(org.id)
                  }}
                >
                  <OrgAvatar name={orgLabel} />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {orgLabel}
                  </span>
                  {isActive && (
                    <Check className="size-4 shrink-0 text-foreground" />
                  )}
                </DropdownMenuItem>
              )
            })}

            <DropdownMenuSeparator />

            {/* Create New Workspace — disabled placeholder (not wired up yet) */}
            <DropdownMenuItem
              disabled
              className="gap-1.5 px-1 py-1.5"
              onSelect={(e) => e.preventDefault()}
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <Plus className="size-4" />
              </div>
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {t('account:menu.create_workspace')}
              </span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {/* Appearance / theme */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer items-start gap-1.5 p-2">
            <Moon className="mt-0.5 size-4 shrink-0 text-foreground" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
              <span className="truncate text-sm">
                {t('account:menu.appearance')}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {t(
                  THEME_OPTIONS.find((o) => o.id === (theme ?? 'system'))
                    ?.labelKey ?? 'common:settings.appearance.system'
                )}
              </span>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {THEME_OPTIONS.map(({ id, icon: Icon, labelKey }) => (
              <DropdownMenuItem
                key={id}
                className="cursor-pointer gap-2"
                onSelect={() => setTheme(id)}
              >
                <Icon className="size-4 text-muted-foreground" />
                <span className="flex-1">{t(labelKey)}</span>
                {theme === id && (
                  <Check className="size-4 shrink-0 text-sky-500" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Language */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer items-start gap-1.5 p-2">
            <Languages className="mt-0.5 size-4 shrink-0 text-foreground" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
              <span className="truncate text-sm">
                {t('account:menu.language')}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {langName(currentLang)}
              </span>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {Object.keys(SUPPORTED_LANGUAGES).map((code) => (
              <DropdownMenuItem
                key={code}
                className="cursor-pointer gap-2"
                onSelect={() => void changeLanguage(code as SupportedLanguage)}
              >
                <span className="flex-1">{langName(code)}</span>
                {currentLang === code && (
                  <Check className="size-4 shrink-0 text-sky-500" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {/* Settings */}
        <DropdownMenuItem
          className="cursor-pointer gap-1.5 p-2"
          onSelect={() => navigate('/settings')}
        >
          <SettingsIcon className="size-4 text-foreground" />
          <span className="flex-1 text-sm">{t('account:settings')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Log out — plain item style to match Settings, per the design */}
        <DropdownMenuItem
          className="cursor-pointer gap-1.5 p-2"
          onSelect={() => void logout()}
        >
          <LogOut className="size-4 text-foreground" />
          <span className="flex-1 text-sm">{t('account:logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
